"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MapPin, MessageSquare, ArrowRight, Sparkles, CircleCheck as CheckCircle2, Search, Check } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const faqCategories = ["All Questions", "Milton Keynes Strategy", "Direct Access & Process", "Google Maps & Rankings"];
const faqs = [{
  id: "faq-1",
  category: "Milton Keynes Strategy",
  question: "Why is Local SEO critical for businesses in Milton Keynes?",
  answer: "Milton Keynes is one of the UK's most competitive local markets with rapidly growing commercial hubs. When local customers search for services in MK, Google prioritizes local map packs and localized organic results. My local SEO strategies place your business directly in front of active high-intent customers across Central Milton Keynes, Bletchley, Newport Pagnell, Stony Stratford, and surrounding areas."
}, {
  id: "faq-2",
  category: "Direct Access & Process",
  question: "Will I work directly with you, Yassine, or an agency account manager?",
  answer: "You work directly with me — Yassine Essaadi. I am an independent freelance SEO consultant, not an agency. I personally execute your audit, design your localized strategy, and perform all technical and content optimizations. You get direct access, fast feedback, and full transparency without junior handoffs."
}, {
  id: "faq-3",
  category: "Milton Keynes Strategy",
  question: "How do you tailor a Local SEO strategy specifically for Milton Keynes?",
  answer: "I analyze your specific local competitor landscape across Milton Keynes postcodes (MK1–MK19). Rather than applying generic templates, I build hyper-local landing pages, structure schema markups tailored to your service areas, optimize localized search intent, and secure genuine Buckinghamshire local citations."
}, {
  id: "faq-4",
  category: "Google Maps & Rankings",
  question: "How do you optimize my Google Business Profile for local map packs?",
  answer: "I conduct a deep audit of your Google Business Profile (GBP), fixing category selections, secondary attributes, geo-tagged photo assets, and local service areas. I also implement review acquisition frameworks and localized citations to push your profile into the top 3 Google Map Pack positions."
}, {
  id: "faq-5",
  category: "Milton Keynes Strategy",
  question: "How quickly can my business expect to see local SEO results?",
  answer: "While SEO is a long-term compound strategy, initial local improvements often appear within 60 to 90 days. GBP optimization and local citation cleanups frequently yield quicker visibility gains in map results, followed by steady organic ranking improvements for competitive local keywords."
}, {
  id: "faq-6",
  category: "Direct Access & Process",
  question: "What makes working with a freelance SEO consultant better than hiring an agency?",
  answer: "Agencies often charge high overhead fees while delegating actual work to junior staff. When you hire me, 100% of your investment goes directly toward senior-level expertise. I am agile, responsive, and personally accountable for delivering measurable search growth and clear ROI."
}, {
  id: "faq-7",
  category: "Google Maps & Rankings",
  question: "Can you help businesses targeting both Milton Keynes and nearby areas?",
  answer: "Yes. I create clear multi-location SEO structures designed to rank in Milton Keynes as well as neighboring key markets like Bedford, Northampton, Leighton Buzzard, and Luton, ensuring search engines understand your complete geographical service boundary."
}, {
  id: "faq-8",
  category: "Direct Access & Process",
  question: "What does your local SEO deliverable and reporting look like?",
  answer: "I provide clean, straightforward monthly reports highlighting real business metrics: rank tracking across local grid coordinates, organic traffic growth, Google Business Profile calls/directions, and direct lead submissions — zero fluff or confusing agency metrics."
}, {
  id: "faq-9",
  category: "Milton Keynes Strategy",
  question: "How do local citations and backlinks help my Milton Keynes search ranking?",
  answer: "Google uses local citations and geographically relevant links to verify that your business physically exists and operates in Milton Keynes. I manually secure clean citations on recognized UK and Buckinghamshire directories to build rock-solid local authority."
}, {
  id: "faq-10",
  category: "Google Maps & Rankings",
  question: "Do you optimize for AI Search experiences alongside traditional local Google Search?",
  answer: "Yes. Modern local search includes AI-driven summaries on Google and mobile search tools. I optimize your content structure, entity relationships, and customer reviews so AI search engines recognize your business as the recommended local provider in Milton Keynes."
}, {
  id: "faq-11",
  category: "Direct Access & Process",
  question: "Do I have to sign a long-term binding lock-in contract?",
  answer: "No. I offer transparent month-to-month consultancy or clear project-based arrangements. My aim is to earn your long-term trust through verified ranking improvements and consistent lead growth, not restrictive contracts."
}, {
  id: "faq-12",
  category: "Direct Access & Process",
  question: "How do we get started with a Milton Keynes local SEO strategy?",
  answer: "It starts with a free 30-minute consultation. We discuss your current local visibility, target search areas in Milton Keynes, and growth goals. I then perform an initial evaluation and outline a tailored roadmap to win higher local rankings."
}];
export default function Localseofaq() {
  const [selectedCategory, setSelectedCategory] = useState("All Questions");
  const filteredFaqs = selectedCategory === "All Questions" ? faqs : faqs.filter(item => item.category === selectedCategory);
  return <section id="localseomiltonkeynesfaq" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      {/* Background Subtle Highlights */}
      <div className="absolute top-0 right-1/4 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 size-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-5xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 bg-secondary text-secondary-foreground border-border rounded-full font-medium text-xs tracking-wide uppercase">
            <MapPin className="size-3.5 text-primary shrink-0" />
            Milton Keynes Local SEO Expertise
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Frequently Asked Questions About{" "}
            <span className="text-primary">Local SEO in Milton Keynes</span>
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Straightforward, conversational answers about how I help local Milton Keynes
            businesses rank higher on Google, win more Map Pack placements, and turn search
            visibility into real inquiries.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {faqCategories.map((category, index) => <div key={category} data-index={index}>
              <Button variant={selectedCategory === category ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(category)} className={`rounded-full text-xs sm:text-sm transition-all duration-200 ${selectedCategory === category ? "bg-primary text-primary-foreground shadow-sm" : "bg-card text-foreground hover:bg-muted border-border"}`}>
                {category}
              </Button>
            </div>)}
        </div>

        {/* Accordion FAQ List */}
        <div className="bg-card rounded-2xl border border-border p-4 sm:p-6 md:p-8 shadow-sm mb-12">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {filteredFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border border-border/80 rounded-xl px-4 sm:px-6 bg-background transition-all duration-200 hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:bg-muted/30">
                <AccordionTrigger className="py-4 sm:py-5 hover:no-underline text-left group">
                  <span className="text-base sm:text-lg font-semibold text-primary group-hover:text-primary/90 transition-colors pr-2 flex items-start gap-2">
                    <HelpCircle className="size-5 text-primary/70 shrink-0 mt-0.5 hidden sm:inline-block" />
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-muted-foreground text-sm sm:text-base leading-relaxed pl-0 sm:pl-7">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* CTA Card */}
        <div className="bg-muted/60 border border-border rounded-2xl p-6 sm:p-8 md:p-10 text-center relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-foreground text-xs font-medium">
              <Sparkles className="size-3.5 text-accent shrink-0" />
              <span>Direct Consultant Access Guaranteed</span>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
              Have a Specific Local SEO Question for Milton Keynes?
            </h3>

            <p className="text-muted-foreground text-sm sm:text-base">
              No sales reps, no junior account managers. Book a free 1-on-1 strategy call directly
              with me, Yassine Essaadi, to discuss your local business goals.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-full font-medium">
                <Link to="/ContactPage">
                  <MessageSquare className="size-4 shrink-0" />
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-full font-medium bg-background">
                <Link to="/CaseStudiesPage">
                  <span>Explore Milton Keynes Case Studies</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
              </Button>
            </div>

            {/* Direct Trust Highlights */}
            <div className="pt-6 border-t border-border/80 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
              <div className="flex items-center justify-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Direct Access to Yassine</span>
              </div>
              <div className="flex items-center justify-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Tailored Milton Keynes Strategy</span>
              </div>
              <div className="flex items-center justify-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Transparent Monthly Reporting</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}